import { Suspense, lazy } from 'react'
import { LoadingOrb } from './components/LoadingOrb'
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { NotFound } from './components/SectionNav'
import Layout from './components/Layout'
import { isConsole } from './lib/surface'
import { useAuth } from './lib/useAuth'
import Account from './pages/Account'
import AuthCallback from './pages/AuthCallback'
import Billing from './pages/Billing'
import DatabaseDetail from './pages/DatabaseDetail'
import Databases from './pages/Databases'
import Login from './pages/Login'
import RequestInvite from './pages/RequestInvite'
import NewService from './pages/NewService'
import Overview from './pages/Overview'
import ServiceDetail from './pages/ServiceDetail'
import Services from './pages/Services'
import Projects, { ProjectDetail } from './pages/Projects'
import Signup from './pages/Signup'
import Support from './pages/Support'
import Mail from './pages/Mail'
import OperatorSupport from './pages/OperatorSupport'
import Experts, { ExpertProgram, ExpertReview } from './pages/Experts'
import ProjectTools, { WorkspaceInvitation } from './pages/ProjectTools'
import { ProtectionCenter, ResourceInsights, ApplicationTemplates } from './pages/Advanced'

// The deploy view pulls in xterm.js, which is most of the bundle and is only
// needed once a developer opens a build log.
const DeployDetail = lazy(() => import('./pages/DeployDetail'))

// The operator console is for the handful of people who run the platform;
// nobody else should download it.
const AdminAnalytics = lazy(() => import('./pages/admin/Analytics'))
const AdminOverview = lazy(() => import('./pages/admin/Overview'))
const AdminUsers = lazy(() => import('./pages/admin/Users'))
const AdminServices = lazy(() => import('./pages/admin/Services'))
const AdminDatabases = lazy(() => import('./pages/admin/Databases'))
const AdminDeploys = lazy(() => import('./pages/admin/Deploys'))
const AdminUser = lazy(() => import('./pages/admin/UserDetail'))
const AdminAudit = lazy(() => import('./pages/admin/Audit'))
const AdminBeta = lazy(() => import('./pages/admin/BetaRequests'))

const lazyFallback = (
  <div className="empty">
    <LoadingOrb />
  </div>
)

/** The server refuses non-operators anyway; this keeps the pages out of sight.
 *  An operator without a second factor is sent to turn it on — the console
 *  would only answer 403 until they do.
 *
 *  On the console's own hostname a non-operator cannot be sent to "/", which
 *  is the console overview: that is a redirect loop. There is nowhere on this
 *  origin for them to go, so they are told so plainly instead. */
function RequireAdmin({ children }: { children: JSX.Element }) {
  const { user } = useAuth()
  if (!user?.is_admin) {
    if (!isConsole) return <Navigate to="/" replace />
    return (
      <div className="empty">
        <h2>Not an operator account</h2>
        <p className="muted">
          This is the TalariCloud operator console. Your account does not have
          access to it. The dashboard for your services is at{' '}
          <a href="https://app.talaricloud.com">app.talaricloud.com</a>.
        </p>
      </div>
    )
  }
  if (!user.two_factor_enabled) return <Navigate to="/account#two-factor" replace />
  return <Suspense fallback={lazyFallback}>{children}</Suspense>
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    // The session check is usually instant, but on a cold refresh it is the
    // first thing anyone sees — so it is the brand, not a bare spinner.
    return (
      <div className="auth splash">
        <LoadingOrb />
      </div>
    )
  }
  if (!user) {
    // Remember where they were headed so login can send them back.
    return <Navigate to="/login" state={{ from: location.pathname + location.search + location.hash }} replace />
  }
  return children
}

/* ---------------------------------------------------------------------------
   The two surfaces.

   Which routes exist at all depends on the hostname, not only on who is
   signed in. On `app.talaricloud.com` the console's routes are not
   registered, so its code is never even fetched; on `admin.talaricloud.com`
   the customer's are not. See `lib/surface.ts` for why the split is by origin
   — in short, `localStorage` is origin-scoped, and that is where the tokens
   live.

   The API is still the authority: every admin endpoint checks `is_admin` and
   a second factor regardless of which bundle asked. This decides what a
   browser downloads and displays, not what it is allowed to do.
   --------------------------------------------------------------------------- */

/** `/admin/users/:id` -> `/customers/:id`, keeping the customer.
 *  A plain <Navigate> cannot: the target needs the id the old URL carried. */
function LegacyCustomer() {
  const { userId } = useParams()
  return <Navigate to={`/customers/${userId}`} replace />
}

const customerRoutes = (
  <>
        <Route path="/" element={<Overview />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:projectId" element={<ProjectDetail />} />
        <Route path="/projects/:projectId/workspace" element={<ProjectTools />} />
        <Route path="/workspace-invitation" element={<WorkspaceInvitation />} />
        <Route path="/protection" element={<ProtectionCenter />} />
        <Route path="/insights" element={<ResourceInsights />} />
        <Route path="/templates" element={<ApplicationTemplates />} />
        <Route path="/support" element={<Support />} />
        <Route path="/mail" element={<Mail />} />
        <Route path="/experts" element={<Experts />} />
        <Route path="/expert-program" element={<ExpertProgram />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/new" element={<NewService />} />
        <Route path="/services/:serviceId/*" element={<ServiceDetail />} />
        <Route
          path="/deploys/:deploymentId"
          element={
            <Suspense
              fallback={
                <div className="empty">
                  <LoadingOrb />
                </div>
              }
            >
              <DeployDetail />
            </Suspense>
          }
        />
        <Route path="/databases" element={<Databases />} />
        <Route path="/databases/:databaseId" element={<DatabaseDetail />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/account" element={<Account />} />
  </>
)

const consoleRoutes = (
  <>
    {/* Clean paths. `/admin/users` was right while the console was a section
        inside the customer dashboard; on its own hostname it repeats itself —
        admin.talaricloud.com/admin/users says "admin" twice and means it
        once. The front door is "/", because the whole site is the console. */}
    <Route path="/" element={<RequireAdmin><AdminOverview /></RequireAdmin>} />
    <Route path="/analytics" element={<RequireAdmin><AdminAnalytics /></RequireAdmin>} />
    <Route path="/customers" element={<RequireAdmin><AdminUsers /></RequireAdmin>} />
    <Route
      path="/customers/:userId"
      element={<RequireAdmin><AdminUser /></RequireAdmin>}
    />
    <Route path="/services" element={<RequireAdmin><AdminServices /></RequireAdmin>} />
    <Route path="/databases" element={<RequireAdmin><AdminDatabases /></RequireAdmin>} />
    <Route path="/deploys" element={<RequireAdmin><AdminDeploys /></RequireAdmin>} />
    <Route path="/invites" element={<RequireAdmin><AdminBeta /></RequireAdmin>} />
    <Route path="/audit" element={<RequireAdmin><AdminAudit /></RequireAdmin>} />
    <Route path="/support" element={<RequireAdmin><Support operator /></RequireAdmin>} />
    <Route path="/support-account/:userId" element={<RequireAdmin><OperatorSupport /></RequireAdmin>} />
    <Route path="/experts" element={<RequireAdmin><ExpertReview /></RequireAdmin>} />

    {/* Not a customer page here: it is where an operator turns on the second
        factor the console requires, so RequireAdmin's redirect has somewhere
        to land. */}
    <Route path="/account" element={<Account />} />

    {/* The paths this console used to have, while it lived inside the
        customer dashboard. Somebody has them bookmarked, and a link in a chat
        from last week should not land on a redirect to the overview with no
        explanation of where the page went. */}
    <Route path="/admin" element={<Navigate to="/" replace />} />
    <Route path="/admin/users" element={<Navigate to="/customers" replace />} />
    <Route path="/admin/users/:userId" element={<LegacyCustomer />} />
    <Route path="/admin/beta" element={<Navigate to="/invites" replace />} />
    <Route path="/admin/audit" element={<Navigate to="/audit" replace />} />
  </>
)

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {!isConsole && <Route path="/request-invite" element={<RequestInvite />} />}
      {/* Not on the console: operator rights are granted to an account that
          already exists, never signed up for. Unregistered rather than
          hidden, so the chunk is never fetched there and the catch-all below
          sends the path to the overview — which sends it to sign in. */}
      {!isConsole && <Route path="/signup" element={<Signup />} />}
      <Route path="/auth/callback" element={<AuthCallback />} />

      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        {isConsole ? consoleRoutes : customerRoutes}
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}
